import {
  PERSON_UPDATE_BREAKDOWN_CREDIT_TYPE,
  PERSON_UPDATE_BREAKDOWN_TITLE,
  PERSON_UPDATE_BREAKDOWN_TOOLTIP,
} from '@aibees/elephant/constants';
import { useBootstrapApiBase } from '@aibees/shared/api';
import { TableProps } from '@aibees/shared/components';
import { roundNumber } from '@aibees/shared/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  PersonEnrichableResponse,
  PersonUpdateBreakdownKey,
  SaveRecordAction,
} from '../types';
import { usePersonEnrichableCountMutationApi } from './use-person-enrichable-count-mutation-api';

export interface BulkUpdatePreflightRow {
  key: PersonUpdateBreakdownKey;
  label: string;
  count: number;
  rate: number;
  subtotal: number;
  // Info-icon copy, shared with the matching Enrich menu item. Absent for a
  // breakdown key with no approved copy — the row then renders no icon.
  tooltip?: string;
}

export interface BulkUpdatePreflight {
  rows: BulkUpdatePreflightRow[];
  // Contacts with no eligible update for the requested actions. Not charged.
  skipped?: number;
  // Server-calculated. Never re-summed from the rows.
  totalCost?: number;
  // Contacts that will actually be updated — the rows are mutually exclusive.
  eligible: number;
  isCalculating: boolean;
  // The request failed. Distinct from "nothing is eligible": both leave every
  // count at zero, and without this the dialog reads a failure as "these
  // contacts have no pending updates". The backend's own error message is
  // already surfaced by useBaseMutation, so this only drives the dialog.
  failed: boolean;
}

/**
 * Prices a Saved Records bulk update before it runs. Hits `?action=cost-estimation`,
 * which spends no credits and runs the same eligibility logic as the real call, so
 * the numbers shown here match the final invoice.
 *
 * Pass `undefined` for `table` while the dialog is closed to skip the request.
 * See docs/saved-records-enrich-actions.md.
 */
export function useBulkUpdatePreflight(
  actions: SaveRecordAction[],
  rowKeys: PersonUpdateBreakdownKey[],
  table?: TableProps
): BulkUpdatePreflight {
  const [data, setData] = useState<PersonEnrichableResponse>();
  const [isCalculating, setIsCalculating] = useState(false);
  const [failed, setFailed] = useState(false);

  const { mutateAsync } = usePersonEnrichableCountMutationApi();
  const cost = useBootstrapApiBase().data?.cost;

  const ids = table?.selection.included;
  // Config entries are module-level constants, but key on the values anyway so an
  // inline array cannot re-trigger the request on every render.
  const actionsKey = actions.join(',');

  // Guards against a slow earlier response overwriting a newer one — selections
  // here can reach 10,000 contacts and responses are not ordered.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!ids?.length) {
      setData(undefined);
      setIsCalculating(false);
      setFailed(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setData(undefined);
    setIsCalculating(true);
    setFailed(false);

    mutateAsync({ ids, actions })
      .then((response) => {
        if (requestIdRef.current === requestId) setData(response);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setData(undefined);
        setFailed(true);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setIsCalculating(false);
      });
    // `actions` is tracked through actionsKey so a new array identity does not
    // refire the request; mutateAsync is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, actionsKey]);

  const rows = useMemo(
    () =>
      rowKeys.map((key) => {
        const count = data?.breakdown[key] ?? 0;
        const rate = cost?.[PERSON_UPDATE_BREAKDOWN_CREDIT_TYPE[key]] ?? 0;

        return {
          key,
          label: PERSON_UPDATE_BREAKDOWN_TITLE[key],
          count,
          rate,
          // Display only — the charge of record is the server's totalCost.
          subtotal: roundNumber(count * rate, 2),
          tooltip: PERSON_UPDATE_BREAKDOWN_TOOLTIP[key]?.(
            (creditType) => cost?.[creditType] ?? 0
          ),
        };
      }),
    [data, rowKeys, cost]
  );

  const eligible = useMemo(
    () => rows.reduce((sum, row) => sum + row.count, 0),
    [rows]
  );

  return {
    rows,
    skipped: data?.skipped,
    totalCost: data?.totalCost,
    eligible,
    isCalculating,
    failed,
  };
}
